/**
 * Global realtime routing table for the admin UI.
 *
 * The backend fans every durable `ClusterEvent` out to socket.io rooms (see
 * the `@cluster/types` room registry). This module is the single client-side
 * table that maps each room to:
 *
 *   - the RTK Query cache tag(s) to invalidate when an event lands
 *     (`ROOM_TAG_MAP`), always including `Dashboard`, and
 *   - the event names the room can emit (`ROOM_EVENT_MAP`), so the global
 *     `RealtimeProvider` can register one listener per event and drop polling.
 *
 * "Persistence is the source of truth; the live stream is latency sugar" — a
 * missed event is always recoverable through a normal RTK Query refetch.
 */

import {
  ALL_REALTIME_ROOMS,
  ROOM_AGENT_FACTORY_UPDATES,
  ROOM_AGENT_RUNTIME_UPDATES,
  ROOM_APPROVALS_UPDATES,
  ROOM_AUTH_UPDATES,
  ROOM_COMMERCE_UPDATES,
  ROOM_CINEMA_UPDATES,
  ROOM_INDEXER_UPDATES,
  ROOM_JOBS_UPDATES,
  ROOM_MEDIA_UPDATES,
  ROOM_ORGANIZATIONS_UPDATES,
  ROOM_POLICIES_UPDATES,
  ROOM_PROJECTS_UPDATES,
  ROOM_REPOSITORIES_UPDATES,
  ROOM_ROLES_UPDATES,
  ROOM_SANDBOXES_UPDATES,
  ROOM_SECRETS_UPDATES,
  ROOM_SERVICES_UPDATES,
  ROOM_SETTINGS_UPDATES,
  ROOM_STORE_EDGE_UPDATES,
  ROOM_VOICE_UPDATES,
  COMMUNICATION_UPDATES_ROOM,
  MEETINGS_UPDATES_ROOM,
  PLANNING_UPDATES_ROOM,
  STORAGE_UPDATES_ROOM,
  AgentFactoryEventType,
  ApprovalsEventType,
  AuthEventType,
  AuthorizationEventType,
  CommunicationEventType,
  CoreEventType,
  DaytonaEventType,
  IndexerEventType,
  MeetingEventType,
  MediaEventType,
  OrganizationsEventType,
  PlanningEventType,
  ProjectEventType,
  RepositoriesEventType,
  RolesEventType,
  SecretsEventType,
  StorageEventType,
  SubjectsEventType,
  VoiceEventType,
} from '@cluster/types';
import type { TagType } from '@/store/api/apiSlice';

export { ALL_REALTIME_ROOMS };

/**
 * Rooms that only exist as gateway constants
 * (`services/gateway/src/ws/ws-agent-stream.constants.ts`) and are not
 * re-exported from `@cluster/types`.
 */
export const MEETINGS_TRANSCRIPT_UPDATES_ROOM = 'meetings:transcript-updates';

/**
 * `voice:updates` was a gateway-only constant until Phase 6 registered
 * `ROOM_VOICE_UPDATES` in the shared room registry (org-scoped per master
 * A13). The alias keeps this module's existing public surface intact while
 * pointing at the single shared constant.
 */
export const VOICE_UPDATES_ROOM = ROOM_VOICE_UPDATES;

/** Conversation/audio event names — owned by `@cluster/agent-core` (literals). */
const CONVERSATION_EVENTS: readonly string[] = [
  'conversation:created',
  'conversation:message-added',
  'conversation:deleted',
  'conversation:forked',
  'conversation:delegated',
  'conversation:completed',
  'agent:audio-ended',
  'conversation:started',
  'conversation:idle',
  'conversation:step-completed',
  'conversation:paused',
  'conversation:resumed',
  'conversation:stopped',
  'conversation:compacted',
  'conversation:delegation-resolved',
  'conversation:failed',
];

/** Bulk child-progress event names (Phase F extends the communication enum later). */
const BULK_EVENTS: readonly string[] = [
  'bulk:completed',
  'bulk:failed',
  'bulk:timeout',
  'bulk:cancelled',
  'bulk:action-completed',
];

/** Communication webhook CRUD event names (Phase F adds the enum members later). */
const WEBHOOK_EVENTS: readonly string[] = [
  'communication:webhook-created',
  'communication:webhook-updated',
  'communication:webhook-deleted',
];

/** Live meeting transcript event name emitted by the gateway bridge (literal). */
const MEETINGS_TRANSCRIPT_UPDATED_EVENT = 'meeting:transcript-updated';

/**
 * Phase 6 catalog v10 voice event names emitted by voice-bridge on the
 * org-scoped `voice:updates:{orgId}` room (no exported enum; literals, same
 * pattern as the commerce/store-edge/cinema literal lists).
 */
const VOICE_CATALOG_EVENTS: readonly string[] = [
  'voice:call-started',
  'voice:call-ended',
  'voice:caller-identified',
  'voice:escalated',
  'voice:approval-readout',
];

/** The literal union of every RTK Query cache tag (defined in `apiSlice`). */
export type { TagType };

/**
 * Every event name each room can emit, used by the provider to register one
 * `wsClient.on(event, ...)` listener per event. Built from the shared string
 * enums; literal strings cover events that have no exported enum.
 */
export const ROOM_EVENT_MAP: Record<string, string[]> = {
  [ROOM_AUTH_UPDATES]: Object.values(AuthEventType),
  [ROOM_COMMERCE_UPDATES]: [
    'orders:order-created',
    'orders:order-closed',
    'orders:order-cancelled',
    'orders:refund-issued',
    'orders:refund-pending',
    'orders:exchange-created',
    'orders:close-compensated',
    'payments:tender-authorized',
    'payments:tender-captured',
    'payments:tender-failed',
    'payments:tender-refunded',
    // Phase 7 subscriptions (P3-T6) — catalog 1.17.0. These ride the existing
    // org-scoped commerce room; the catalog deliberately adds no new room.
    'subscriptions:subscribed',
    'subscriptions:activated',
    'subscriptions:billing-succeeded',
    'subscriptions:billing-failed',
    'subscriptions:past-due',
    'subscriptions:grace',
    'subscriptions:suspended',
    'subscriptions:restored',
    'subscriptions:cancelled',
    'subscriptions:expired',
    'subscriptions:entitlement-limit-hit',
  ],
  [ROOM_STORE_EDGE_UPDATES]: [
    'pos-rooms:room-created',
    'pos-rooms:room-updated',
    'pos-rooms:node-registered',
    'pos-rooms:node-promoted',
    'pos-rooms:node-demoted',
    'pos-rooms:node-disabled',
    'pos-rooms:node-enabled',
    'pos-rooms:commands-issued',
    'pos-rooms:ingest-anomaly',
    'pos-rooms:review-item-created',
    'edge:mesh-degraded',
  ],
  [ROOM_CINEMA_UPDATES]: [
    'ticketing:showtime-scheduled',
    'ticketing:showtime-cancelled',
    'ticketing:seat-held',
    'ticketing:seat-released',
    'ticketing:seat-sold',
    'ticketing:seat-state-changed',
    'ticketing:capacity-held',
    'ticketing:capacity-released',
  ],
  [ROOM_POLICIES_UPDATES]: Object.values(AuthorizationEventType),
  [ROOM_ORGANIZATIONS_UPDATES]: Object.values(OrganizationsEventType),
  [ROOM_ROLES_UPDATES]: Object.values(RolesEventType),
  [ROOM_APPROVALS_UPDATES]: Object.values(ApprovalsEventType),
  [ROOM_SECRETS_UPDATES]: Object.values(SecretsEventType),
  [ROOM_PROJECTS_UPDATES]: Object.values(ProjectEventType),
  [ROOM_AGENT_FACTORY_UPDATES]: Object.values(AgentFactoryEventType),
  [ROOM_AGENT_RUNTIME_UPDATES]: [...CONVERSATION_EVENTS],
  [ROOM_REPOSITORIES_UPDATES]: Object.values(RepositoriesEventType),
  [ROOM_INDEXER_UPDATES]: Object.values(IndexerEventType),
  [ROOM_SANDBOXES_UPDATES]: Object.values(DaytonaEventType),
  [ROOM_SERVICES_UPDATES]: Object.values(SubjectsEventType),
  [ROOM_JOBS_UPDATES]: Object.values(CoreEventType),
  [ROOM_SETTINGS_UPDATES]: [CoreEventType.FEATURE_FLAG_TOGGLED],
  [STORAGE_UPDATES_ROOM]: Object.values(StorageEventType),
  // Phase 8b torrent lifecycle (P5-T06, catalog 1.19.0): the five
  // `storage:torrent-*` members arrived via StorageEventType (mounted /
  // unmounted / promoted / revival-seeded / unavailable) — the enum-driven
  // list above picks them up with zero drift. Tag invalidation rides the
  // existing StorageFile entry in ROOM_TAG_MAP (mount state is a file-level
  // property; no new tag was needed in TAG_TYPES).
  [PLANNING_UPDATES_ROOM]: Object.values(PlanningEventType),
  [MEETINGS_UPDATES_ROOM]: Object.values(MeetingEventType),
  [MEETINGS_TRANSCRIPT_UPDATES_ROOM]: [MEETINGS_TRANSCRIPT_UPDATED_EVENT],
  [VOICE_UPDATES_ROOM]: [...Object.values(VoiceEventType), ...VOICE_CATALOG_EVENTS],
  // Phase 8a media (P4-T06, catalog 1.18.0 "v12" completion + the 1.16.0
  // subset): every MediaEventType member maps to the org-scoped
  // `media:updates` room. The gateway bridge emits the raw event type as the
  // socket.io event name on that room; `media:job-progress` is included here
  // because it arrives over the dedicated Redis bridge (it stays EXCLUDED
  // from durable fan-out, not from WS delivery).
  // Phase 8b (P5-T06, catalog 1.19.0): `media:packaging-completed` and
  // `media:packaging-failed` joined the enum and ride the same map.
  // `media:playback-token-issued` is STRIPPED (same defensive idiom as the
  // SENSITIVE_EVENT_SET filter below): it fires per customer playback start
  // and per token refresh, and the provider invalidates every event's room
  // tags — an admin dashboard has no per-token view and must not refetch
  // Media/Dashboard on each mint. WS delivery of the tick is untouched for
  // clients that register it themselves.
  [ROOM_MEDIA_UPDATES]: Object.values(MediaEventType).filter(
    (eventType) => eventType !== MediaEventType.PLAYBACK_TOKEN_ISSUED,
  ),
  [COMMUNICATION_UPDATES_ROOM]: [
    ...Object.values(CommunicationEventType),
    ...WEBHOOK_EVENTS,
    ...BULK_EVENTS,
  ],
};

/**
 * Sensitive event names that must NEVER drive global cache invalidation, even
 * for admins (defense-in-depth on top of the gateway's admin-only rooms rule):
 * vault reads, audit-log writes and raw audio transcripts can carry content
 * that should not round-trip through the generic fan-out listeners.
 */
export const SENSITIVE_REALTIME_EVENTS: readonly string[] = [
  'secrets:vault-read',
  'authorization:audit-logged',
  'agent:audio-transcript',
];

const SENSITIVE_EVENT_SET: ReadonlySet<string> = new Set(SENSITIVE_REALTIME_EVENTS);

// Strip sensitive event names from every room's listener list so the provider
// never registers a handler for them.
for (const room of Object.keys(ROOM_EVENT_MAP)) {
  ROOM_EVENT_MAP[room] = ROOM_EVENT_MAP[room].filter(
    (eventType) => !SENSITIVE_EVENT_SET.has(eventType),
  );
}

/**
 * Every room the global provider subscribes to: all fanned-out rooms
 * (`ALL_REALTIME_ROOMS`) plus the raw-channel rooms handled by dedicated
 * gateway bridges (storage/planning/meetings/voice/communication) so that
 * pages using those tags update live without polling.
 */
export const REALTIME_ROOMS: readonly string[] = Array.from(
  new Set<string>([...ALL_REALTIME_ROOMS, ...Object.keys(ROOM_EVENT_MAP)]),
);

/**
 * Room → RTK Query tag(s) to invalidate. Every entry includes `Dashboard` so
 * aggregate widgets stay fresh. Tags are the literal union `TagType` (derived
 * from `apiSlice`'s tag tuple), so a typo is a compile error.
 */
export const ROOM_TAG_MAP: Record<string, TagType[]> = {
  [ROOM_AUTH_UPDATES]: ['User', 'ApiKey', 'Dashboard'],
  [ROOM_COMMERCE_UPDATES]: ['Order', 'Tender', 'Subscription', 'Dashboard'],
  [ROOM_STORE_EDGE_UPDATES]: ['StoreEdge', 'Dashboard'],
  [ROOM_CINEMA_UPDATES]: ['Ticketing', 'Dashboard'],
  [ROOM_POLICIES_UPDATES]: ['Policy', 'Dashboard'],
  [ROOM_ORGANIZATIONS_UPDATES]: [
    'Organization',
    'OrganizationFull',
    'OrgMember',
    'UserGroup',
    'UserGroupMember',
    'Dashboard',
  ],
  [ROOM_ROLES_UPDATES]: ['Role', 'RoleAssignment', 'Dashboard'],
  [ROOM_APPROVALS_UPDATES]: ['Approval', 'Dashboard'],
  [ROOM_SECRETS_UPDATES]: ['Vault', 'Dashboard'],
  [ROOM_PROJECTS_UPDATES]: ['Project', 'ProjectResource', 'Planning', 'Dashboard'],
  [ROOM_AGENT_FACTORY_UPDATES]: [
    'AgentProvider',
    'AgentModel',
    'AgentModelCollection',
    'AgentFactory',
    'AgentTrigger',
    'Dashboard',
  ],
  [ROOM_AGENT_RUNTIME_UPDATES]: [
    'AgentConversation',
    'AgentMessage',
    'AgentRun',
    'AgentQueue',
    'Dashboard',
  ],
  [ROOM_REPOSITORIES_UPDATES]: ['Repository', 'Branch', 'Instance', 'IndexJob', 'Dashboard'],
  [ROOM_INDEXER_UPDATES]: [
    'IndexerSource',
    'IndexJob',
    'IndexerGraph',
    'IndexerSearch',
    'Dashboard',
  ],
  [ROOM_SANDBOXES_UPDATES]: ['Sandbox', 'Dashboard'],
  [ROOM_SERVICES_UPDATES]: ['Service', 'Dashboard'],
  [ROOM_JOBS_UPDATES]: ['Job', 'AgentQueue', 'Dashboard'],
  [ROOM_SETTINGS_UPDATES]: ['Settings', 'FeatureFlag', 'Dashboard'],
  [STORAGE_UPDATES_ROOM]: [
    'StorageSource',
    'StorageAccount',
    'StorageCollection',
    'StorageFile',
    'StorageUpload',
    'StorageProvider',
    'IndexerSource',
    'Dashboard',
  ],
  [PLANNING_UPDATES_ROOM]: ['Planning', 'Dashboard'],
  [MEETINGS_UPDATES_ROOM]: ['Meeting', 'Dashboard'],
  [MEETINGS_TRANSCRIPT_UPDATES_ROOM]: ['Meeting', 'Dashboard'],
  [VOICE_UPDATES_ROOM]: ['Voice', 'Dashboard'],
  // Media catalog events (P4-T06) invalidate the Media tag (media pages) and
  // the dashboard widgets.
  [ROOM_MEDIA_UPDATES]: ['Media', 'Dashboard'],
  [COMMUNICATION_UPDATES_ROOM]: ['Webhook', 'WebhookLog', 'BulkOperation', 'Dashboard'],
};

/**
 * Resolve the RTK Query tags invalidated by an event on `room`. Returns an
 * empty array for unknown rooms so the provider can never throw.
 */
export function mapEventToTags(room: string, _eventType: string): TagType[] {
  return ROOM_TAG_MAP[room] ?? [];
}
